/** The project context rows (project_context / project_color / project_font / project_font_face) plus the setup facts the onboarding plan reads. */
import { z } from "zod";

// Server-measured truths the onboarding plan reads; never trusted from disk.
export const projectContextFactsSchema = z
  .object({
    brand: z.boolean(),
    business: z.boolean(),
    payments: z.boolean(),
    site: z.boolean(),
    domain: z.boolean(),
    integrations: z.array(z.string()),
  })
  .describe("ProjectContextFacts");

export const projectContextCategorySourceSchema = z
  .enum(["agent", "user", "import"])
  .describe("ProjectContextCategorySource");

/** Who wrote a context row: the signup pregen, a person, or the agent; the import keeps the files' own provenance. */
export const projectContextOriginSchema = z
  .enum(["pregen", "user", "agent"])
  .describe("ProjectContextOrigin");
export type ProjectContextOrigin = z.infer<typeof projectContextOriginSchema>;

export const projectContextPricePointSchema = z
  .enum(["budget", "mid-market", "premium", "luxury"])
  .describe("ProjectContextPricePoint");

const UNKNOWN_TEXT = new Set(["unknown", "n/a"]);

/** Extraction writes "unknown" for a fact it could not find; the column holds null. */
export function knownText(value: string | null | undefined): string | null {
  const text = value?.trim() ?? "";
  return text === "" || UNKNOWN_TEXT.has(text.toLowerCase()) ? null : text;
}

/** Legacy files list voice and business model; the columns hold one sentence. */
export function listText(
  value: string | string[] | null | undefined,
): string | null {
  const items = Array.isArray(value) ? value : [value ?? ""];
  const text = items
    .map((item) => item.trim())
    .filter((item) => item !== "")
    .join(", ");
  return text === "" ? null : text.slice(0, 512);
}

export const businessColumnsSchema = z
  .object({
    name: z
      .string()
      .max(256)
      .nullable()
      .describe("Brand name as the business writes it"),
    about: z.string().max(512).nullable().describe("One-line description"),
    audience: z.string().max(512).nullable().describe("Who buys, in one line"),
    pricePoint: projectContextPricePointSchema
      .nullable()
      .describe("Price positioning"),
    businessModel: z
      .string()
      .max(512)
      .nullable()
      .describe(
        "How the business makes money, in a sentence (dtc, retail, subscription, wholesale, …); null when unknown",
      ),
    foundedYear: z
      .number()
      .int()
      .min(1000)
      .max(9999)
      .nullable()
      .describe("Founding year, null when unknown"),
    location: z
      .string()
      .max(256)
      .nullable()
      .describe('"City, Country", null when unknown'),
    voice: z
      .string()
      .max(512)
      .nullable()
      .describe(
        "How the brand sounds, in a sentence or a few adjectives (warm, direct); null when unknown",
      ),
    sourceUrl: z
      .string()
      .max(1024)
      .nullable()
      .describe("Site the facts were extracted from"),
  })
  .describe("BusinessColumns");

export const projectColorKeySchema = z
  .string()
  .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/u)
  .max(64)
  .describe("Stable slug key, e.g. action or gray-100");

export const projectColorHexSchema = z
  .string()
  .regex(/^#[0-9A-Fa-f]{6}$/u)
  .describe("#RRGGBB");

export const projectColorCategorySchema = z
  .enum(["brand", "neutral"])
  .describe("ProjectColorCategory");

export const projectColorSchema = z
  .object({
    key: projectColorKeySchema,
    name: z.string().min(1).max(128).describe("Editable label"),
    hex: projectColorHexSchema,
    category: projectColorCategorySchema.describe("brand or neutral"),
    roles: z
      .array(z.string().max(32))
      .describe(
        "Semantic roles this color fills (primary, background, …); one color per role per context",
      ),
    usage: z.string().max(512).nullable().describe("Human and agent guidance"),
    position: z.number().int().nonnegative().describe("Display order"),
  })
  .describe("ProjectColor");

export const projectFontDeliverySchema = z
  .enum(["google-fonts", "cdn", "uploaded", "system", "unknown"])
  .describe("ProjectFontDelivery");

export const projectFontStyleSchema = z
  .enum(["normal", "italic"])
  .describe("ProjectFontStyle");

export const projectFontFaceSchema = z
  .object({
    weight: z.number().int().min(100).max(900).describe("100 to 900"),
    style: projectFontStyleSchema.describe("normal or italic"),
    assetId: z
      .uuid()
      .nullable()
      .describe(
        "Approved font asset holding the file; null for Google or system fonts",
      ),
    sourceUrl: z
      .string()
      .max(1024)
      .nullable()
      .describe("Where the face file came from"),
  })
  .describe("ProjectFontFace");

export const projectFontSchema = z
  .object({
    key: projectColorKeySchema.describe("Stable slug key, e.g. inter"),
    family: z.string().min(1).max(128).describe("Font family name"),
    roles: z
      .array(z.string().max(32))
      .describe("heading, body, mono, …; one font per role per context"),
    delivery: projectFontDeliverySchema.describe("How the font is served"),
    license: z.string().max(64).nullable().describe("License name"),
    usage: z.string().max(512).nullable().describe("Guidance"),
    position: z.number().int().nonnegative().describe("Display order"),
    faces: z.array(projectFontFaceSchema).describe("Weight/style faces"),
  })
  .describe("ProjectFont");

/** "The weeknight cook" -> "Weeknight cook": a nickname is a segment label, so the article comes off wherever one is written. */
export function withoutLeadingArticle(nickname: string): string {
  const stripped = nickname.trim().replace(/^the\s+/iu, "");
  return stripped.charAt(0).toUpperCase() + stripped.slice(1);
}

export const projectPersonaAwarenessSchema = z
  .enum(["unaware", "problem", "solution", "product", "most"])
  .describe("ProjectPersonaAwareness");

export const projectPersonaAvatarLibrarySchema = z
  .enum(["dicebear-critters"])
  .describe("ProjectPersonaAvatarLibrary");

/** The editable columns as an object, so views can extend it; `projectPersonaSchema` adds the age-range check. */
export const projectPersonaColumnsSchema = z
  .object({
    key: projectColorKeySchema.describe("Stable slug key, e.g. weeknight-cook"),
    nickname: z
      .string()
      .min(1)
      .max(48)
      .describe(
        "Segment label, two to four words, e.g. The weeknight cook; never a person's name",
      ),
    headline: z
      .string()
      .min(1)
      .max(160)
      .describe("Situation plus problem in the buyer's own words, one line"),
    ageGte: z
      .number()
      .int()
      .min(0)
      .max(120)
      .nullable()
      .describe("Youngest age in the range; null when unknown"),
    ageLt: z
      .number()
      .int()
      .min(1)
      .max(121)
      .nullable()
      .describe(
        "Exclusive upper age: 35 to 54 is ageGte 35, ageLt 55; null for open-ended",
      ),
    awareness: projectPersonaAwarenessSchema
      .nullable()
      .describe("Where this buyer starts"),
    sharePercent: z
      .number()
      .int()
      .min(0)
      .max(100)
      .nullable()
      .describe("Estimated share of buyers; the set need not sum to 100"),
    quote: z
      .string()
      .max(512)
      .nullable()
      .describe("In the customer's words, from the brand's reviews or copy"),
    quoteSource: z
      .string()
      .max(120)
      .nullable()
      .describe(
        "Who said it, as the card attributes it: a first name or 'a verified review'",
      ),
    quoteSourceUrl: z
      .url()
      .max(1024)
      .nullable()
      .describe("The page the quote came from"),
    descriptionMarkdown: z
      .string()
      .max(8192)
      .describe(
        "Markdown: ## Goals, ## Pains, ## Objections, ## Buying triggers; rendered as a document, never parsed",
      ),
  })
  .describe("ProjectPersonaColumns");

/** What a person or the agent may set; the avatar and evidence columns are server-owned. */
export const projectPersonaSchema = projectPersonaColumnsSchema
  .refine(
    (persona) => {
      return (
        persona.ageGte === null ||
        persona.ageLt === null ||
        persona.ageGte < persona.ageLt
      );
    },
    { path: ["ageLt"], message: "ageLt must be greater than ageGte" },
  )
  .describe("ProjectPersona");

const projectPersonaEvidenceEntrySchema = z.object({
  value: z.union([z.string(), z.number(), z.null()]),
  reason: z.string().max(280),
});

/** Per typed field: the value generation produced and one sentence on where it came from. Never edited. */
export const projectPersonaEvidenceSchema = z
  .object({
    nickname: projectPersonaEvidenceEntrySchema.optional(),
    headline: projectPersonaEvidenceEntrySchema.optional(),
    ageGte: projectPersonaEvidenceEntrySchema.optional(),
    ageLt: projectPersonaEvidenceEntrySchema.optional(),
    awareness: projectPersonaEvidenceEntrySchema.optional(),
    sharePercent: projectPersonaEvidenceEntrySchema.optional(),
    quote: projectPersonaEvidenceEntrySchema.optional(),
  })
  .describe("ProjectPersonaEvidence");

export const projectPersonaViewSchema = projectPersonaColumnsSchema
  .extend({
    avatarLibrary: projectPersonaAvatarLibrarySchema,
    avatarJson: z.record(z.string(), z.unknown()),
    avatarSvg: z.string(),
    evidence: projectPersonaEvidenceSchema.nullable(),
  })
  .describe("ProjectPersonaView");

// Critters variant names, verbatim, so the model's output maps straight onto generator options.
export const personaTraitsSchema = z
  .object({
    body: z.enum([
      "dome",
      "block",
      "tower",
      "chimney",
      "squat",
      "blob",
      "round",
      "tilt",
      "lean",
      "peak",
      "bell",
      "wedge",
      "steps",
    ]),
    top: z.enum([
      "none",
      "horns",
      "hornsSmall",
      "spike",
      "antenna",
      "antennae",
      "earsRound",
      "earsPointy",
      "earsDroop",
      "spikes",
      "fin",
      "crown",
      "sprout",
      "nub",
      "bobble",
    ]),
    eyes: z.enum([
      "round",
      "dots",
      "bigPupils",
      "sideeye",
      "happy",
      "closedLine",
      "wink",
      "squint",
      "wide",
      "sleepy",
      "uneven",
      "mono",
    ]),
    mouth: z.enum([
      "smile",
      "tinySmile",
      "grin",
      "laugh",
      "ooh",
      "line",
      "smirk",
      "wavy",
      "frown",
      "open",
      "tongue",
    ]),
    cheeks: z.enum(["none", "blush", "freckles"]),
  })
  .describe("PersonaTraits");

const personaListSchema = z.array(z.string().min(1).max(160)).min(1).max(5);

/** The model's reply to one generation call: the personas plus, per persona, its creature traits and where each field came from. */
export const personaExtractionSchema = z
  .object({
    personas: z
      .array(
        z.object({
          nickname: projectPersonaColumnsSchema.shape.nickname,
          headline: projectPersonaColumnsSchema.shape.headline,
          ageGte: projectPersonaColumnsSchema.shape.ageGte,
          ageLt: projectPersonaColumnsSchema.shape.ageLt,
          awareness: projectPersonaAwarenessSchema,
          sharePercent: z.number().int().min(0).max(100),
          quote: projectPersonaColumnsSchema.shape.quote,
          quoteSource: projectPersonaColumnsSchema.shape.quoteSource,
          // A plain string here: OpenAI-family response_format rejects `format: uri`; the service validates the URL before it becomes a column.
          quoteSourceUrl: z.string().max(1024).nullable(),
          goals: personaListSchema,
          pains: personaListSchema,
          objections: personaListSchema,
          triggers: personaListSchema,
          traits: personaTraitsSchema,
          reasons: z.object({
            headline: z.string().max(280),
            age: z.string().max(280),
            awareness: z.string().max(280),
            sharePercent: z.string().max(280),
            quote: z.string().max(280).nullable(),
          }),
        }),
      )
      .min(3)
      .max(5),
    confidence: z.enum(["high", "medium", "low"]),
  })
  .describe("PersonaExtraction");

export const PERSONA_AWARENESS_LABELS: Record<ProjectPersonaAwareness, string> =
  {
    unaware: "Unaware",
    problem: "Problem-aware",
    solution: "Solution-aware",
    product: "Product-aware",
    most: "Most-aware",
  };

/** "28 to 44", "55+", "Under 30", or "Any age" when the range is open on both ends. */
export function personaAgeLabel({
  ageGte,
  ageLt,
}: {
  ageGte: number | null;
  ageLt: number | null;
}): string {
  if (ageGte === null && ageLt === null) {
    return "Any age";
  }
  if (ageLt === null || ageLt > 80) {
    return `${ageGte ?? 18}+`;
  }
  if (ageGte === null) {
    return `Under ${ageLt}`;
  }
  return `${ageGte} to ${ageLt - 1}`;
}

/** The four description sections, in the order the card and the agent read them. */
export function renderPersonaDescription({
  goals,
  pains,
  objections,
  triggers,
}: {
  goals: string[];
  pains: string[];
  objections: string[];
  triggers: string[];
}): string {
  const section = (title: string, items: string[]) => {
    return `## ${title}\n${items.map((item) => `- ${item}`).join("\n")}`;
  };
  return [
    section("Goals", goals),
    section("Pains", pains),
    section("Objections", objections),
    section("Buying triggers", triggers),
  ].join("\n\n");
}

// The hard-coded tag vocabulary for brand context. A tag names what a Drive
// file (or an asset) is for, and a file may carry several. Branding's
// context sections and the agent's `update-brand` tagFile op both read this
// list, so a new group is a new slug here and nowhere else.
export const PROJECT_CONTEXT_TAGS = ["business", "design", "context"] as const;

export const projectContextTagSchema = z
  .enum(PROJECT_CONTEXT_TAGS)
  .describe("ProjectContextTag");

export function isProjectContextTag(value: string): value is ProjectContextTag {
  return projectContextTagSchema.safeParse(value).success;
}

/** The brand tags a file carries, in vocabulary order; any other tag on the file is ignored. */
export function projectContextTagsOf(
  tags: readonly string[],
): ProjectContextTag[] {
  return PROJECT_CONTEXT_TAGS.filter((tag) => tags.includes(tag));
}

export type ProjectContextCategorySource = z.infer<
  typeof projectContextCategorySourceSchema
>;
export type ProjectContextPricePoint = z.infer<
  typeof projectContextPricePointSchema
>;
export type BusinessColumns = z.infer<typeof businessColumnsSchema>;
export type ProjectColorInput = z.infer<typeof projectColorSchema>;
export type ProjectFontFaceInput = z.infer<typeof projectFontFaceSchema>;
export type ProjectFontInput = z.infer<typeof projectFontSchema>;
export type ProjectContextTag = z.infer<typeof projectContextTagSchema>;
export type ProjectPersonaInput = z.infer<typeof projectPersonaSchema>;
export type ProjectPersonaView = z.infer<typeof projectPersonaViewSchema>;
export type ProjectPersonaEvidence = z.infer<
  typeof projectPersonaEvidenceSchema
>;
export type ProjectPersonaAwareness = z.infer<
  typeof projectPersonaAwarenessSchema
>;
export type PersonaTraits = z.infer<typeof personaTraitsSchema>;
export type PersonaExtraction = z.infer<typeof personaExtractionSchema>;
